import { apolloClient, queries, refreshStoredAccessToken } from "@/apollo";
import { Announcement, AppMode } from "@/CodegenTypes";
import { useRootStore } from "@/store/pinia";
import { hc } from "hono/client";
import decode from "jwt-decode";
import type { AppType } from "../../edge-api/src/app";

type SettingsRpcClient = ReturnType<typeof hc<AppType>>;
type JsonResponse<T> = {
  ok: boolean;
  status: number;
  json(): Promise<T>;
};

class SettingsRpcError extends Error {
  constructor(public status: number) {
    super(`Settings RPC failed with ${status}`);
  }
}

let cachedClient: SettingsRpcClient | null = null;
let cachedEndpoint = "";
const ACCESS_TOKEN_REFRESH_SKEW_SECONDS = 30;

function isHonoSettingsEnabled() {
  return (
    process.env.VUE_APP_HONO_RPC_ENABLED === "true" &&
    process.env.VUE_APP_HONO_RPC_SETTINGS_ENABLED === "true" &&
    !!process.env.VUE_APP_HONO_RPC_ENDPOINT
  );
}

function getClient() {
  const endpoint = process.env.VUE_APP_HONO_RPC_ENDPOINT?.replace(/\/$/, "");
  if (!endpoint) {
    throw new Error("Missing VUE_APP_HONO_RPC_ENDPOINT");
  }
  if (!cachedClient || cachedEndpoint !== endpoint) {
    cachedEndpoint = endpoint;
    cachedClient = hc<AppType>(endpoint);
  }
  return cachedClient;
}

function getRequestInit() {
  const token = useRootStore().accessToken;
  return {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : undefined,
  };
}

function isAccessTokenStale(token: string): boolean {
  try {
    const payload = decode<{ exp?: number }>(token);
    if (!payload.exp) return false;

    return (
      payload.exp <=
      Math.floor(Date.now() / 1000) + ACCESS_TOKEN_REFRESH_SKEW_SECONDS
    );
  } catch {
    return false;
  }
}

async function refreshAccessTokenForRpc() {
  const store = useRootStore();
  if (!store.refreshToken) return null;

  return refreshStoredAccessToken();
}

async function refreshStaleAccessTokenForRpc() {
  const store = useRootStore();
  if (store.accessToken) {
    if (!isAccessTokenStale(store.accessToken)) return;
  }

  await refreshAccessTokenForRpc();
}

async function readJson<T>(response: JsonResponse<T>): Promise<T> {
  if (!response.ok) {
    throw new SettingsRpcError(response.status);
  }
  return response.json() as Promise<T>;
}

async function withGraphqlFallback<T>(
  honoRead: () => Promise<T>,
  graphqlRead: () => Promise<T>
): Promise<T> {
  if (isHonoSettingsEnabled()) {
    try {
      await refreshStaleAccessTokenForRpc();
      return await honoRead();
    } catch (error) {
      if (error instanceof SettingsRpcError && error.status === 401) {
        try {
          const accessToken = await refreshAccessTokenForRpc();
          if (accessToken) {
            return await honoRead();
          }
        } catch {
          // GraphQL remains the availability fallback while RPC is in pilot mode.
        }
      }
      // GraphQL remains the availability fallback while RPC is in pilot mode.
    }
  }
  return graphqlRead();
}

export async function fetchTnc(): Promise<string> {
  return withGraphqlFallback(
    async () => {
      const data = await readJson<{ tnc: string }>(
        await getClient().settings.tnc.$get({}, getRequestInit())
      );
      return data.tnc;
    },
    async () => {
      const result = await apolloClient.query<{ tnc: string }>({
        query: queries.tnc,
        fetchPolicy: "network-only",
      });
      return result.data.tnc;
    }
  );
}

export async function fetchScrollingText(appMode: AppMode): Promise<string> {
  return withGraphqlFallback(
    async () => {
      const data = await readJson<{ scrollingText: string }>(
        await getClient().settings["scrolling-text"].$get(
          { query: { appMode } },
          getRequestInit()
        )
      );
      return data.scrollingText;
    },
    async () => {
      const result = await apolloClient.query<{ scrollingText: string }>({
        query: queries.scrollingText,
        variables: { appMode },
        fetchPolicy: "network-only",
      });
      return result.data.scrollingText;
    }
  );
}

export async function fetchTelegramBotName(appMode: AppMode): Promise<string> {
  return withGraphqlFallback(
    async () => {
      const data = await readJson<{ telegramBotName: string }>(
        await getClient().settings["telegram-bot-name"].$get(
          { query: { appMode } },
          getRequestInit()
        )
      );
      return data.telegramBotName;
    },
    async () => {
      const result = await apolloClient.query<{ telegramBotName: string }>({
        query: queries.telegramBotName,
        variables: { appMode },
        fetchPolicy: "network-only",
      });
      return result.data.telegramBotName;
    }
  );
}

export async function fetchAnnouncements(
  appMode: AppMode
): Promise<Announcement[]> {
  return withGraphqlFallback(
    async () => {
      const data = await readJson<{ announcements: Announcement[] }>(
        await getClient().settings.announcements.$get(
          { query: { appMode } },
          getRequestInit()
        )
      );
      return data.announcements;
    },
    async () => {
      const result = await apolloClient.query<{
        announcements: Announcement[];
      }>({
        query: queries.announcements,
        variables: { appMode },
        fetchPolicy: "network-only",
      });
      return result.data.announcements;
    }
  );
}
